import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  Modal,
  Table,
  Tag,
  Typography,
  Space,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { postReq, getReq } from "../api.js";
import dayjs from "dayjs";

const { Title } = Typography;

const statusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "success";
    case "REJECTED":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

const TeamLeavesTable = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 1,
    search: "",
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await postReq("/user/getTeamLeaves", {
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      setRows(res?.data?.data);
      setTotal(res?.data?.total);
    } catch (err) {
      message.error(err?.message || "Error fetching leaves");
    } finally {
      setLoading(false);
    }
  };

  const approveLeave = async (id) => {
    try {
      const res = await getReq(`user/approveLeave/${id}`);
      if (res.status === 200) {
        message.success(res.message || "Leave Approved");
        fetchLeaves();
      } else {
        message.error(res.message || "Error approving leave");
      }
    } catch (error) {
      message.error(error.message || "Error approving leave");
    }
  };

  const rejectLeave = async (id) => {
    try {
      const res = await getReq(`user/rejectLeave/${id}`);
      if (res.status === 200) {
        message.success(res.message || "Leave Rejected");
        fetchLeaves();
      } else {
        message.error(res.message || "Error rejecting leave");
      }
    } catch (error) {
      message.error(error.message || "Error rejecting leave");
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedLeaveId) return;
    setActionLoading(true);
    if (actionType === "approve") await approveLeave(selectedLeaveId);
    if (actionType === "reject") await rejectLeave(selectedLeaveId);
    setActionLoading(false);
    setOpenConfirm(false);
    setSelectedLeaveId(null);
    setActionType(null);
  };

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search, page: 1 }));
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchLeaves();
  }, [paginationModel]);

  const columns = useMemo(
    () => [
      {
        title: "Employee",
        key: "employee",
        width: 180,
        render: (_, row) => `${row.user?.name ?? ""}` || "-",
      },
      {
        title: "Leave Type",
        dataIndex: "leaveType",
        key: "leaveType",
        width: 130,
      },
      {
        title: "From",
        dataIndex: "fromDate",
        key: "fromDate",
        width: 120,
        render: (val) => dayjs(val).format("DD MMM YYYY"),
      },
      {
        title: "To",
        dataIndex: "toDate",
        key: "toDate",
        width: 120,
        render: (val) => dayjs(val).format("DD MMM YYYY"),
      },
      {
        title: "Days",
        dataIndex: "quantity",
        key: "quantity",
        width: 80,
      },
      {
        title: "Approved At",
        dataIndex: "approvedAt",
        key: "approvedAt",
        width: 140,
        render: (val) => (val ? dayjs(val).format("DD MMM YYYY") : "-"),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 130,
        render: (status) => (
          <Tag color={statusColor(status)} style={{ fontWeight: 500 }}>
            {status}
          </Tag>
        ),
      },
      {
        title: "Action",
        key: "action",
        width: 120,
        render: (_, row) => (
          <Space size={4}>
            <Button
              type="text"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={
                <CheckOutlined
                  style={{
                    color: row.status === "PENDING" ? "#52c41a" : undefined,
                  }}
                />
              }
              disabled={row.status !== "PENDING"}
              onClick={() => {
                setSelectedLeaveId(row._id);
                setActionType("approve");
                setOpenConfirm(true);
              }}
            />
            <Button
              type="text"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={
                <CloseOutlined
                  style={{
                    color: row.status === "PENDING" ? "#ff4d4f" : undefined,
                  }}
                />
              }
              disabled={row.status !== "PENDING"}
              onClick={() => {
                setSelectedLeaveId(row._id);
                setActionType("reject");
                setOpenConfirm(true);
              }}
            />
          </Space>
        ),
      },
    ],
    [],
  );

  const isApprove = actionType === "approve";

  return (
    <Card
      bordered={false}
      styles={{ body: { padding: "24px 40px" } }}
      style={{ flex: 1 }}
    >
      <Input
        placeholder="Search Team Member"
        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 240, marginBottom: 24 }}
      />

      <Table
        loading={loading}
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row._id}
        scroll={{ x: "max-content" }}
        pagination={{
          current: paginationModel.page,
          pageSize: paginationModel.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20"],
          showTotal: (tot) => `Total ${tot} records`,
          onChange: (page, pageSize) =>
              setPaginationModel((prev) => ({ ...prev, page, pageSize })),
        }}
      />

      {/* Confirm Modal */}
      <Modal
        open={openConfirm}
        onCancel={() => {
          setOpenConfirm(false);
          setSelectedLeaveId(null);
          setActionType(null);
        }}
        title={isApprove ? "Approve Leave" : "Reject Leave"}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setOpenConfirm(false);
              setSelectedLeaveId(null);
              setActionType(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger={!isApprove}
            style={
              isApprove
                  ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                  : {}
            }
            loading={actionLoading}
            onClick={handleConfirmAction}
          >
            {isApprove ? "Approve" : "Reject"}
          </Button>,
        ]}
      >
        <Typography.Text>
          Are you sure you want to {actionType} this leave request?
        </Typography.Text>
      </Modal>
    </Card>
  );
};

export default TeamLeavesTable;
